import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Invitation, InviteVerification, SendInviteRequest } from '../types/invitation';

export const invitationKeys = {
  all: ['invitations'] as const,
  mine: (reunionId: string) => ['invitations', 'mine', reunionId] as const,
  verify: (token: string) => ['invitations', 'verify', token] as const,
};

export function useMyInvitations(reunionId: string) {
  return useQuery({
    queryKey: invitationKeys.mine(reunionId),
    queryFn: () =>
      api.get<{ data: Invitation[] }>('/invitations/mine', { reunionId }).then(r => r.data),
    enabled: !!reunionId,
  });
}

export function useSendInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SendInviteRequest) =>
      api.post<{ data: Invitation }>('/invitations', data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.put<{ data: Invitation }>(`/invitations/${invitationId}/resend`).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
}

export function useVerifyInviteToken(token: string | null) {
  return useQuery({
    queryKey: invitationKeys.verify(token || ''),
    queryFn: () =>
      api.get<{ data: InviteVerification }>(`/invitations/verify/${token}`).then(r => r.data),
    enabled: !!token,
    retry: false,
  });
}
